import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useCourse } from "../../../../context/CourseContext";
import { useUtilityContext } from "../../../../context/UtilityContext";
import {
  Pencil,
  X,
  Plus,
  ChevronDown,
  FileText,
  Library,
  Trash2,
} from "lucide-react";
import SaveButton from "../../../../utils/CourseSaveButton";
import { useParams } from "react-router-dom";
import {
  card,
  cardTitle,
  microLabel,
  bodySecondary,
  accentBarSection,
  dividerInner,
  emptyState,
  motionFadeUp,
  motionStaggerChild,
  iconSize,
  pageTitle,
} from "../../../../lib/design-system";
import { cn } from "../../../../lib/utils";

const SyllabusManager = () => {
  const {
    courseData,
    addModule,
    updateModuleTitle,
    addTopicToModule,
    updateTopic,
    removeTopic,
    removeModule,
  } = useCourse();

  const [editingModule, setEditingModule] = useState(null);
  const [editingTopic, setEditingTopic] = useState({
    moduleNumber: null,
    topicIndex: null,
  });
  const [expandedModule, setExpandedModule] = useState(null);
  const { courseID: urlCourseID } = useParams();
  const { selectedCourseId } = useUtilityContext();
  const courseID = urlCourseID || selectedCourseId;

  const handleModuleTitleEdit = (moduleNumber, newTitle) => {
    updateModuleTitle(moduleNumber, newTitle);
    setEditingModule(null);
  };

  const handleTopicEdit = (moduleNumber, topicIndex, newTopic) => {
    updateTopic(moduleNumber, topicIndex, newTopic);
    setEditingTopic({ moduleNumber: null, topicIndex: null });
  };

  const modules = courseData?.syllabus?.modules || [];
  const totalTopics = modules.reduce((sum, m) => sum + (m.topics?.length || 0), 0);

  return (
    <motion.div {...motionFadeUp} className="mx-auto space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={pageTitle}>Course Syllabus</h1>
          <p className={cn(bodySecondary, "mt-1")}>
            {modules.length} module{modules.length !== 1 ? "s" : ""} &middot; {totalTopics} topic{totalTopics !== 1 ? "s" : ""}
          </p>
          <div className={cn(accentBarSection, "mt-3")} />
        </div>
        <SaveButton urlId={courseID} />
      </div>

      {/* Modules — timeline layout */}
      {modules.length > 0 ? (
        <div className="relative">
          {/* Timeline rail */}
          <div className="absolute left-[19px] top-6 bottom-6 w-px bg-gray-200 dark:bg-gray-700 hidden sm:block" />

          <div className="space-y-3">
            {modules.map((module, moduleIndex) => {
              const isExpanded = expandedModule === module.moduleNumber;
              const topicCount = module?.topics?.length || 0;

              return (
                <motion.div
                  key={module.moduleNumber}
                  {...motionStaggerChild(moduleIndex)}
                  className="relative"
                >
                  {/* Module card */}
                  <div className={cn(card, "overflow-hidden sm:ml-10")}>
                    {/* Timeline dot */}
                    <div className="absolute left-[12px] top-5 w-[15px] h-[15px] rounded-sm bg-accent1 border-2 border-white dark:border-gray-900 z-10 hidden sm:block" />

                    {/* Module header */}
                    <button
                      className={cn(
                        "w-full text-left px-4 sm:px-5 py-4 flex items-center gap-3 transition-colors",
                        isExpanded
                          ? "bg-accent1/5 dark:bg-accent1/10"
                          : "hover:bg-gray-50 dark:hover:bg-gray-800"
                      )}
                      onClick={() =>
                        setExpandedModule(isExpanded ? null : module.moduleNumber)
                      }
                    >
                      {/* Module number */}
                      <div className="flex-shrink-0 w-9 h-9 rounded-sm bg-accent1/10 dark:bg-accent1/20 flex items-center justify-center">
                        <span className="text-sm font-black text-accent1">
                          {module.moduleNumber}
                        </span>
                      </div>

                      {/* Title */}
                      <div className="flex-1 min-w-0">
                        {editingModule === module.moduleNumber ? (
                          <input
                            type="text"
                            className="w-full bg-transparent border-b-2 border-accent1/40 focus:border-accent1 px-0.5 py-0.5 text-sm font-bold text-gray-900 dark:text-white outline-none transition-colors"
                            defaultValue={module.moduleTitle}
                            onBlur={(e) =>
                              handleModuleTitleEdit(module.moduleNumber, e.target.value)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter")
                                handleModuleTitleEdit(module.moduleNumber, e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                          />
                        ) : (
                          <h3 className="text-sm font-bold text-gray-900 dark:text-white truncate">
                            {module.moduleTitle}
                          </h3>
                        )}
                        <p className={cn(bodySecondary, "text-[11px] mt-0.5")}>
                          {topicCount} topic{topicCount !== 1 ? "s" : ""}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <span
                          role="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingModule(module.moduleNumber);
                          }}
                          className="p-1.5 hover:bg-accent1/10 dark:hover:bg-accent1/20 rounded-sm text-accent1 transition-colors cursor-pointer"
                        >
                          <Pencil className={iconSize.sm} />
                        </span>
                        <span
                          role="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeModule(module.moduleNumber);
                          }}
                          className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-sm text-red-500 dark:text-red-400 transition-colors cursor-pointer"
                        >
                          <Trash2 className={iconSize.sm} />
                        </span>
                        <div
                          className={cn(
                            "p-1.5 rounded-sm transition-transform duration-200",
                            isExpanded && "rotate-180"
                          )}
                        >
                          <ChevronDown
                            className={cn(iconSize.sm, "text-gray-400 dark:text-gray-500")}
                          />
                        </div>
                      </div>
                    </button>

                    {/* Expanded topics */}
                    <AnimatePresence initial={false}>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className={cn("border-t px-4 sm:px-5 py-4", dividerInner)}>
                            {topicCount > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {module.topics.map((topic, topicIndex) => (
                                  <div
                                    key={topicIndex}
                                    className="group relative inline-flex items-center gap-1.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-sm px-3 py-1.5 hover:border-accent1/30 dark:hover:border-accent1/40 transition-colors"
                                  >
                                    {editingTopic.moduleNumber === module.moduleNumber &&
                                    editingTopic.topicIndex === topicIndex ? (
                                      <input
                                        type="text"
                                        className="bg-transparent border-none outline-none text-sm text-gray-900 dark:text-white min-w-[120px]"
                                        defaultValue={topic}
                                        onBlur={(e) =>
                                          handleTopicEdit(
                                            module.moduleNumber,
                                            topicIndex,
                                            e.target.value
                                          )
                                        }
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter")
                                            handleTopicEdit(
                                              module.moduleNumber,
                                              topicIndex,
                                              e.target.value
                                            );
                                        }}
                                        autoFocus
                                      />
                                    ) : (
                                      <>
                                        <FileText className="h-3 w-3 text-accent1/50 flex-shrink-0" />
                                        <span className="text-sm text-gray-700 dark:text-gray-200">
                                          {topic}
                                        </span>

                                        {/* Hover actions */}
                                        <div className="hidden group-hover:flex items-center gap-0.5 ml-1">
                                          <button
                                            onClick={() =>
                                              setEditingTopic({
                                                moduleNumber: module.moduleNumber,
                                                topicIndex,
                                              })
                                            }
                                            className="p-0.5 hover:bg-accent1/10 rounded-sm text-accent1 transition-colors"
                                          >
                                            <Pencil className="h-2.5 w-2.5" />
                                          </button>
                                          <button
                                            onClick={() =>
                                              removeTopic(module.moduleNumber, topicIndex)
                                            }
                                            className="p-0.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-sm text-red-400 transition-colors"
                                          >
                                            <X className="h-2.5 w-2.5" />
                                          </button>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                ))}

                                {/* Add topic inline */}
                                <button
                                  onClick={() => addTopicToModule(module.moduleNumber)}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border-2 border-dashed border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 hover:border-accent1 hover:text-accent1 transition-colors text-sm"
                                >
                                  <Plus className="h-3 w-3" />
                                  Add topic
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-3">
                                <p className={cn(bodySecondary, "text-sm")}>
                                  No topics yet.
                                </p>
                                <button
                                  onClick={() => addTopicToModule(module.moduleNumber)}
                                  className="inline-flex items-center gap-1 text-xs font-bold text-accent1 hover:text-accent1/80 uppercase tracking-wider transition-colors"
                                >
                                  <Plus className="h-3 w-3" />
                                  Add first topic
                                </button>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className={emptyState}>
          <Library
            className={cn(
              iconSize.empty,
              "text-gray-300 dark:text-gray-600 mx-auto mb-3"
            )}
          />
          <h3 className={cn(cardTitle, "mb-1")}>No Modules Created Yet</h3>
          <p className={cn(bodySecondary, "mb-0")}>
            Start building your course syllabus by adding modules and topics.
          </p>
        </div>
      )}

      {/* Add Module button */}
      <button
        onClick={addModule}
        className="flex items-center justify-center w-full bg-accent1 text-white px-6 py-3 rounded-sm hover:bg-accent1/90 transition-colors font-bold uppercase tracking-wider text-xs shadow-sm hover:shadow-md"
      >
        <Plus className={cn(iconSize.md, "mr-2")} />
        Add New Module
      </button>
    </motion.div>
  );
};

export default SyllabusManager;
